import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { trackPageView, updatePageDuration } from "@/lib/analytics.functions";

export function AnalyticsTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const track = useServerFn(trackPageView);
  const updateDuration = useServerFn(updatePageDuration);

  useEffect(() => {
    // 1. Skip admin routes
    if (pathname.startsWith("/admin")) return;

    // 2. Get or create visitor ID
    let visitorId = localStorage.getItem("lww_visitor_id");
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem("lww_visitor_id", visitorId);
    }

    let sessionId = sessionStorage.getItem("lww_session_id") || undefined;
    let pageViewId: string | null = null;
    let startTime = Date.now();
    let durationInterval: ReturnType<typeof setInterval>;

    const initTracking = async () => {
      try {
        const result = await track({
          data: {
            visitorId,
            path: pathname,
            title: document.title,
            referrer: document.referrer,
            sessionId,
          }
        });

        if (result.sessionId) {
          sessionStorage.setItem("lww_session_id", result.sessionId);
          sessionId = result.sessionId;
        }
        pageViewId = result.pageViewId;

        // 3. Update duration every 10 seconds
        durationInterval = setInterval(async () => {
          if (pageViewId) {
            const duration = Math.floor((Date.now() - startTime) / 1000);
            await updateDuration({ data: { pageViewId, duration } });
          }
        }, 10000);
      } catch (err) {
        console.error("Analytics error:", err);
      }
    };

    // Defer non-essential analytics tracking until the browser is idle
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => initTracking());
    } else {
      setTimeout(initTracking, 2000);
    }

    return () => {
      clearInterval(durationInterval);
      if (pageViewId) {
        const duration = Math.floor((Date.now() - startTime) / 1000);
        // Fire and forget exit update
        updateDuration({ data: { pageViewId, duration, isExit: true } }).catch(() => {});
      }
    };
  }, [pathname, track, updateDuration]);

  return null;
}
